import React, { createContext, useContext, useEffect, useState } from 'react';
import { authApi, settingsApi } from '../api';

const AuthContext = createContext(null);

const DEFAULT_THEME = { primary: '#9C3D54', accent: '#7A2E41' };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('fm_user');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [theme, setThemeState] = useState(DEFAULT_THEME);
  const [density, setDensityState] = useState(() => localStorage.getItem('fm_density') || 'comfortable');
  const [booting, setBooting] = useState(true);

  // Fetch theme from backend on load
  useEffect(() => {
    settingsApi.getTheme()
      .then(t => setThemeState({ primary: t.primary || DEFAULT_THEME.primary, accent: t.accent || DEFAULT_THEME.accent }))
      .catch(() => {})
      .finally(() => setBooting(false));
  }, []);

  // Verify token / refresh user on load
  useEffect(() => {
    const token = localStorage.getItem('fm_token');
    if (token) {
      authApi.me().then(r => {
        setUser(r.user);
        localStorage.setItem('fm_user', JSON.stringify(r.user));
      }).catch(() => {
        localStorage.removeItem('fm_user');
        localStorage.removeItem('fm_token');
        setUser(null);
      });
    }
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--fm-primary', theme.primary);
    document.documentElement.style.setProperty('--fm-accent', theme.accent);
  }, [theme]);

  // Sync density preference to a root-level data attribute so table CSS
  // (see index.css [data-density=compact] rules) applies globally without
  // needing to prop-drill through every page.
  useEffect(() => {
    document.documentElement.dataset.density = density;
    localStorage.setItem('fm_density', density);
  }, [density]);

  const setDensity = (next) => setDensityState(next === 'compact' ? 'compact' : 'comfortable');
  const toggleDensity = () => setDensityState(d => (d === 'compact' ? 'comfortable' : 'compact'));

  const signIn = (userObj, token) => {
    localStorage.setItem('fm_token', token);
    localStorage.setItem('fm_user', JSON.stringify(userObj));
    setUser(userObj);
  };

  const signOut = () => {
    localStorage.removeItem('fm_token');
    localStorage.removeItem('fm_user');
    setUser(null);
  };

  const setTheme = async (t) => {
    await settingsApi.setTheme(t);
    setThemeState(t);
  };

  return (
    <AuthContext.Provider value={{ user, signIn, signOut, theme, setTheme, density, setDensity, toggleDensity, booting }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
